/**
 * Foto KTP Page
 *
 * Page for reviewing member foto and KTP uploads. Supports grid and table views.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/data-table";
import { ChevronLeft, ChevronRight, Search, LayoutGrid, List, AlertCircle } from "lucide-react";
import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";
import { toast } from "sonner";
import { ImageZoomModal } from "@/components/common/ImageZoomModal";
import { FotoKtpGrid, type FotoKtpMember } from "./components/FotoKtpGrid";
import { FotoKtpSheet } from "./components/FotoKtpSheet";
import { createFotoKtpColumns } from "./columns/fotoKtpColumns";

interface FotoKtpResponse {
  data: FotoKtpMember[];
  nextCursor: string | null;
  previousCursor: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
}

export function FotoKtpPage() {
  const [activeTab, setActiveTab] = React.useState<"semua" | "hold">("semua");
  const [viewMode, setViewMode] = React.useState<"grid" | "table">("grid");
  const [searchQuery, setSearchQuery] = React.useState("");

  // Data state
  const [members, setMembers] = React.useState<FotoKtpMember[]>([]);
  const [holdMembers, setHoldMembers] = React.useState<FotoKtpMember[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = React.useState<string | null>(null);
  const [hasNext, setHasNext] = React.useState(false);
  const [hasPrevious, setHasPrevious] = React.useState(false);
  const [holdNextCursor, setHoldNextCursor] = React.useState<string | null>(null);
  const [holdPreviousCursor, setHoldPreviousCursor] = React.useState<string | null>(null);
  const [holdHasNext, setHoldHasNext] = React.useState(false);
  const [holdHasPrevious, setHoldHasPrevious] = React.useState(false);
  const [holdCurrentCursor, setHoldCurrentCursor] = React.useState<string | null>(null);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Zoom state
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const [zoomSrc, setZoomSrc] = React.useState<string | null>(null);
  const [zoomTitle, setZoomTitle] = React.useState("");

  // Sheet state
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [sheetMember, setSheetMember] = React.useState<FotoKtpMember | null>(null);
  const [sheetMode, setSheetMode] = React.useState<"reject" | "approve">("reject");
  const [sheetLoading, setSheetLoading] = React.useState(false);

  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    document.title = "Foto KTP - ORARI";
  }, []);

  const fetchPage = async (direction: "next" | "previous", cursor: string | null | undefined, tabFilter: "semua" | "hold" = "semua") => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      params.append("direction", direction);
      if (tabFilter === "hold") params.append("filter", "rejected");
      if (searchQuery.trim()) params.append("search", searchQuery.trim());

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/foto-ktp?${params.toString()}`,
        { method: "GET", credentials: "include", headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) throw new Error("Failed to fetch foto KTP data");

      const data: FotoKtpResponse = await response.json();

      if (tabFilter === "hold") {
        setHoldMembers(data.data);
        setHoldNextCursor(data.nextCursor);
        setHoldPreviousCursor(data.previousCursor);
        setHoldHasNext(data.hasNext);
        setHoldHasPrevious(data.hasPrevious);
        setHoldCurrentCursor(cursor || null);
      } else {
        setMembers(data.data);
        setNextCursor(data.nextCursor);
        setPreviousCursor(data.previousCursor);
        setHasNext(data.hasNext);
        setHasPrevious(data.hasPrevious);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error";
      setError(errorMsg);
      toast.error("Gagal memuat data foto KTP: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchPage("next", undefined, "semua");
  }, []);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchPage("next", undefined, activeTab);
  }, [searchQuery]);

  React.useEffect(() => {
    if (activeTab === "hold" && holdMembers.length === 0) {
      fetchPage("next", undefined, "hold");
    }
  }, [activeTab]);

  const handleNextPage = () => {
    if (activeTab === "hold") {
      if (holdNextCursor) fetchPage("next", holdNextCursor, "hold");
    } else {
      if (nextCursor) fetchPage("next", nextCursor, "semua");
    }
  };

  const handlePreviousPage = () => {
    if (activeTab === "hold") {
      if (holdPreviousCursor) fetchPage("previous", holdPreviousCursor, "hold");
    } else {
      if (previousCursor) fetchPage("previous", previousCursor, "semua");
    }
  };

  const handleZoom = (src: string, title: string) => {
    setZoomSrc(src);
    setZoomTitle(title);
    setZoomOpen(true);
  };

  const handleAction = (member: FotoKtpMember, mode: "reject" | "approve") => {
    setSheetMember(member);
    setSheetMode(mode);
    setSheetOpen(true);
  };

  const handleSheetConfirm = async (member: FotoKtpMember, mode: "reject" | "approve", reason?: string) => {
    setSheetLoading(true);
    try {
      const endpoint = mode === "reject"
        ? `${apiConfig.baseURL}/api/users/${member._id}/reject-foto`
        : `${apiConfig.baseURL}/api/users/${member._id}/approve-foto`;

      const res = await apiFetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: mode === "reject" ? JSON.stringify({ reason }) : JSON.stringify({}),
      });

      if (!res.ok) throw new Error(mode === "reject" ? "Gagal menandai foto" : "Gagal melepas hold foto");

      toast.success(
        mode === "reject"
          ? `Foto ${member.callsign} ditandai tidak memenuhi kriteria`
          : `Foto ${member.callsign} telah dilepas dari hold`
      );

      setSheetOpen(false);
      setSheetMember(null);

      // Refresh
      if (activeTab === "hold") {
        fetchPage("next", holdCurrentCursor || undefined, "hold");
      } else {
        fetchPage("next", undefined, "semua");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal");
    } finally {
      setSheetLoading(false);
    }
  };

  const currentMembers = activeTab === "hold" ? holdMembers : members;
  const currentHasNext = activeTab === "hold" ? holdHasNext : hasNext;
  const currentHasPrevious = activeTab === "hold" ? holdHasPrevious : hasPrevious;

  const columns = React.useMemo(
    () => createFotoKtpColumns({ onZoom: handleZoom, onAction: handleAction, tabMode: activeTab }),
    [activeTab]
  );

  return (
    <div className="flex-1 space-y-4 p-6 overflow-x-hidden max-w-[1400px] mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Foto KTP</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Daftar anggota dengan foto profil dan KTP yang telah diunggah
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Cari callsign atau nama..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "secondary" : "ghost"}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setViewMode("table")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "semua" | "hold")}>
              <TabsList>
                <TabsTrigger value="semua">Semua</TabsTrigger>
                <TabsTrigger value="hold">
                  Hold
                  {members.some((m) => m.isFotoValid === false) && (
                    <Badge variant="destructive" className="ml-2 h-5 px-1 text-xs">
                      {members.filter((m) => m.isFotoValid === false).length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Grid View */}
          {viewMode === "grid" && (
            <FotoKtpGrid
              members={currentMembers}
              loading={loading}
              tabMode={activeTab}
              onZoom={handleZoom}
              onAction={handleAction}
            />
          )}

          {/* Table View */}
          {viewMode === "table" && (
            <DataTable
              columns={columns}
              data={currentMembers}
              loading={loading}
              emptyMessage={activeTab === "hold" ? "Tidak ada foto yang ditolak" : "Tidak ada data foto KTP"}
            />
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Menampilkan {currentMembers.length} anggota
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={!currentHasPrevious || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Sebelumnya
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!currentHasNext || loading}
              >
                Berikutnya
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zoom Modal */}
      <ImageZoomModal
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        src={zoomSrc}
        alt="Zoomed image"
        title={zoomTitle}
      />

      {/* Reject/Approve Sheet */}
      <FotoKtpSheet
        member={sheetMember}
        mode={sheetMode}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onConfirm={handleSheetConfirm}
        loading={sheetLoading}
      />
    </div>
  );
}
